/**
 * Single source of truth for turning a membership pricing option into the
 * visit-card-vs-recurring semantics that every storefront surface renders:
 * which field is the quantity (`visits` vs `intervalCount`), the per-unit
 * price divisor, and the pluralized unit noun ("N visits" vs "N months").
 *
 * Callers keep their own presentation — currency token ("EUR " vs "€"),
 * per-unit suffix ("/mo" vs "/month"), ranges, "From …", and markup — and read
 * the shared semantics from here so they cannot drift apart. Free of runtime
 * dependencies (the type-only import below is erased) so it is importable from
 * web today and mobile later.
 */

import type { PaymentMode } from "@lemonadetraining/contracts";

/** The two membership kinds a product can be. */
export type MembershipKind = "recurring" | "visit_card";

/**
 * The subset of a pricing option this module reads. Kept structural (rather
 * than importing the canonical `PricingOption`) so the looser local option
 * shapes the call sites already use are all assignable.
 */
export interface PricingOptionLike {
  amount: number;
  /** "month" for recurring memberships, "one_time" for visit cards. */
  interval?: string;
  /** Number of billing months for recurring memberships. */
  intervalCount?: number;
  /** Number of visits for visit cards. */
  visits?: number;
  /**
   * Commitment term in months for commitment cells. The authoritative term
   * source: monthly-billed commitment cells carry the per-month price, so
   * their `intervalCount` is 1 regardless of term — term length must never
   * be inferred from `intervalCount` alone, nor parsed out of the pricing
   * key string (key formats change; this field is the contract).
   */
  commitmentMonths?: number;
  /**
   * Which payment lane the cell bills on. Absent on old-model and visit-card
   * cells, and on commitment cells written before the field existed — read it
   * through `optionPaymentMode`, never directly.
   */
  paymentMode?: PaymentMode;
}

/** Semantic parts of a pricing option, free of any currency/markup decisions. */
export interface PricingOptionParts {
  isVisitCard: boolean;
  /** Quantity: visits for a visit card, months for a recurring membership (min 1). */
  count: number;
  unit: "visit" | "month";
  /** Pluralized quantity, e.g. "1 visit", "3 months". */
  unitLabel: string;
  /** The option's full price. */
  totalAmount: number;
  /** Price per visit / per month (`totalAmount / count`). */
  perUnitAmount: number;
}

/**
 * Ordering value for a pricing option: commitment term when present, else
 * legacy billing months, else visit count. Exists so list orderings never
 * fall back to parsing the option's key string — monthly-billed commitment
 * cells all share `intervalCount: 1`, and only `commitmentMonths` orders
 * them correctly.
 */
export function pricingOptionSortValue(option: PricingOptionLike): number {
  return option.commitmentMonths ?? option.intervalCount ?? option.visits ?? 0;
}

/**
 * Pluralize a quantity + unit noun. `count === 1` stays singular; every other
 * value (including 0) gets a trailing "s".
 */
export function formatUnitLabel(count: number, unit: "visit" | "month"): string {
  return `${count} ${unit}${count === 1 ? "" : "s"}`;
}

/**
 * Describe a pricing option's visit-card-vs-recurring semantics.
 *
 * @param kind  Optional override. The marketing site discriminates on the
 *   option's own `interval`, so it omits this and lets detection key off
 *   `interval === "one_time"`. The dashboard discriminates on the product-level
 *   `membershipType`, so it passes that here to preserve its exact branch
 *   regardless of the option's `interval` field.
 */
export function describePricingOption(
  option: PricingOptionLike,
  kind?: MembershipKind
): PricingOptionParts {
  const isVisitCard = kind != null ? kind === "visit_card" : option.interval === "one_time";
  const totalAmount = option.amount;

  if (isVisitCard) {
    const count = option.visits || 1;
    return {
      isVisitCard: true,
      count,
      unit: "visit",
      unitLabel: formatUnitLabel(count, "visit"),
      totalAmount,
      perUnitAmount: totalAmount / count,
    };
  }

  const count = option.intervalCount || 1;
  return {
    isVisitCard: false,
    count,
    unit: "month",
    unitLabel: formatUnitLabel(count, "month"),
    totalAmount,
    perUnitAmount: totalAmount / count,
  };
}

/**
 * The shortest term that counts as a commitment. A one-month "term" locks the
 * member into nothing they could not cancel anyway, so it is the plain
 * no-commitment monthly price and shares the old-model cell shape.
 */
export const MIN_COMMITMENT_MONTHS = 2;

/**
 * Whether an option is a commitment cell. Term length is read from
 * `commitmentMonths` only — the pricing key is a lookup convention, not a
 * contract, so nothing here may parse meaning out of it.
 */
export function isCommitmentOption(option: PricingOptionLike): boolean {
  const months = Number(option.commitmentMonths);
  return Number.isInteger(months) && months >= MIN_COMMITMENT_MONTHS;
}

/**
 * The payment lane an option bills on, read from the BILLING SHAPE first: an
 * upfront cell bills once per term (`intervalCount === commitmentMonths`), a
 * monthly cell bills one month at a time.
 *
 * The shape is ground truth, and this mirrors the canonical server rule in
 * `functions/commitmentPricing.js` (`derivePaymentMode`) deliberately — the
 * read-time gate in `functions/commitmentSchedules.js` REFUSES to bind a cell
 * whose declared `paymentMode` disagrees with its shape, so a disagreeing cell
 * is invalid, not a second opinion. Trusting the declared field over the shape
 * would file such a cell in a lane it does not bill in and quote a term total
 * off by the length of the term.
 *
 * The declared field answers only when the shape cannot: a cell carrying no
 * usable `intervalCount` (nothing the server would ever have provisioned) is
 * better read as what it claims than as a silent "monthly".
 *
 * Non-commitment options have no lane to choose and read as "monthly".
 */
export function optionPaymentMode(option: PricingOptionLike): PaymentMode {
  if (!isCommitmentOption(option)) return "monthly";

  const intervalCount = Number(option.intervalCount);
  if (Number.isInteger(intervalCount) && intervalCount > 0) {
    return intervalCount === Number(option.commitmentMonths) ? "upfront" : "monthly";
  }

  if (option.paymentMode === "upfront" || option.paymentMode === "monthly") {
    return option.paymentMode;
  }
  return "monthly";
}

/** A pricing option's semantics with its commitment term resolved. */
export interface CommitmentOptionParts extends PricingOptionParts {
  isCommitment: boolean;
  /** Committed term in months; 1 when the option carries no commitment. */
  commitmentMonths: number;
  /** The lane the option bills on; "monthly" for non-commitment options. */
  paymentMode: PaymentMode;
}

/**
 * `describePricingOption` with commitment terms resolved.
 *
 * The plain describer reads the BILLING period, which is the whole story for
 * old-model options but only half of one for a commitment cell: a 12-month
 * plan billed monthly carries `intervalCount: 1`, so the plain describer would
 * call it "1 month" and report one month's price as the total. Storefront
 * surfaces need the term the member is agreeing to and the money they will
 * part with over it, so they read through here instead.
 *
 * A non-commitment option passes through completely unchanged, which is what
 * makes this a safe swap on surfaces that must stay identical for
 * grandfathered products.
 */
export function describeCommitmentOption(
  option: PricingOptionLike,
  kind?: MembershipKind
): CommitmentOptionParts {
  if (!isCommitmentOption(option)) {
    return {
      ...describePricingOption(option, kind),
      isCommitment: false,
      commitmentMonths: 1,
      paymentMode: "monthly",
    };
  }

  const commitmentMonths = Number(option.commitmentMonths);
  const paymentMode = optionPaymentMode(option);
  // Upfront cells carry the whole term's price; monthly cells carry one
  // month's. Each derives the other from the term rather than dividing a
  // figure it already holds exactly.
  const totalAmount = paymentMode === "upfront" ? option.amount : option.amount * commitmentMonths;
  const perUnitAmount =
    paymentMode === "upfront" ? option.amount / commitmentMonths : option.amount;

  return {
    isVisitCard: false,
    count: commitmentMonths,
    unit: "month",
    unitLabel: formatUnitLabel(commitmentMonths, "month"),
    totalAmount,
    perUnitAmount,
    isCommitment: true,
    commitmentMonths,
    paymentMode,
  };
}

/**
 * Format a cart `selectedOptionKey` ("3_visit", "1_month") into its human label.
 * Returns null for a missing or malformed key (fewer than two underscore-joined
 * parts); an unrecognized unit token passes the raw key through unchanged.
 */
export function formatSelectedOptionKey(key?: string): string | null {
  if (!key) return null;

  const parts = key.split("_");
  if (parts.length < 2) return null;

  const count = parseInt(parts[0], 10);
  const unit = parts[1];

  if (unit === "month") return formatUnitLabel(count, "month");
  if (unit === "visit") return formatUnitLabel(count, "visit");

  return key;
}
